import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { householdService, reviewService } from '@/services';
import type { ProposalTarget } from '@/services/email.service';
import type { HouseholdReviewItem } from '@/services/review.service';
import { useEmailsChanged } from '@/hooks/useEmailsChanged';
import { useUserStore } from '@/stores/userStore';
import type { Child } from '@/types';
import {
  buildReviewList,
  confirmGateFor,
  countItems,
  type ConfirmGate,
  type OpenScheduleTask,
  type ReviewListItem,
} from './reviewListModel';

export interface UseReviewListResult {
  items: ReviewListItem[];
  count: number;
  gate: ConfirmGate;
  loading: boolean;
  error: Error | null;
  /** Silent refetch (realtime hint, tab return, after an action). */
  refresh(): Promise<void>;
  /** Local removals so a card leaves at once (C5); cleared by refresh(). */
  hide(itemId: string): void;
  unhide(itemId: string): void;
  /** Every pending row read, by id — the editor's `initialData`. */
  rows: ReadonlyMap<string, HouseholdReviewItem>;
  /** The household's children (already read for the change diff). */
  children: Child[];
}

const NO_ROWS: ReadonlyMap<string, HouseholdReviewItem> = new Map();
const NO_CHILDREN: Child[] = [];

interface ReviewListData {
  items: HouseholdReviewItem[];
  targets: Map<string, ProposalTarget>;
  targetsFailed: boolean;
  /** Open tasks by cancelled event id (E3). */
  openTasks: Map<string, OpenScheduleTask[]>;
  children: Child[];
}

interface LoadState {
  for: string;
  data: ReviewListData | null;
  error: Error | null;
}

const NO_HIDDEN: ReadonlySet<string> = new Set();

/**
 * Review v0 (YAR-551): the household's one Review list, kept live.
 *
 * One read (`getHouseholdReviewItemsWithTargets`, plus the household's
 * children for change diffs) re-run on mount, on any `emails` change
 * (realtime, own channel) and on return to the tab — the last two are also
 * what bring the list back when an item arrives after Confirm all
 * (YAR-555 AC9). Every refetch after the first is silent: a failure keeps
 * what is on screen.
 */
export function useReviewList(
  householdId: string | null,
  {
    onOutsideChange,
  }: {
    /**
     * Called with each re-read the outside world triggers (an `emails`
     * change, a return to the tab), so the page can re-read what moves
     * with it (the plan count) without a second realtime channel.
     */
    onOutsideChange?: () => void;
  } = {},
): UseReviewListResult {
  const timezone = useUserStore((s) => s.timezone);
  // The latest read that answered, tagged with the household it was for, so
  // nothing from a previous household is ever shown and "loading" holds
  // until the first read for the CURRENT id has landed.
  const [state, setState] = useState<LoadState | null>(null);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(NO_HIDDEN);
  const requestRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!householdId) return;
    const request = ++requestRef.current;
    try {
      const [list, children] = await Promise.all([
        reviewService.getHouseholdReviewItemsWithTargets(householdId),
        // Only the change diff's children line needs them.
        householdService.getChildren(householdId).catch((): Child[] => []),
      ]);
      // Only the latest read may land.
      if (request !== requestRef.current) return;
      setState({ for: householdId, data: { ...list, children }, error: null });
      setHidden(NO_HIDDEN);
    } catch (err) {
      if (request !== requestRef.current) return;
      const error = err instanceof Error ? err : new Error(String(err));
      // A blip on a background read must not replace a list on screen.
      setState((prev) =>
        prev && prev.for === householdId && prev.data
          ? prev
          : { for: householdId, data: null, error },
      );
    }
  }, [householdId]);

  useEffect(() => {
    // Invalidate any read still in flight for the previous household.
    requestRef.current += 1;
    setHidden(NO_HIDDEN);
    void refresh();
  }, [householdId, refresh]);

  const current = state && state.for === householdId ? state : null;
  const loading = !!householdId && !current;
  const data = current?.data ?? null;
  const error = current?.error ?? null;

  const onOutsideChangeRef = useRef(onOutsideChange);
  useEffect(() => {
    onOutsideChangeRef.current = onOutsideChange;
  });

  useEmailsChanged(
    () => {
      void refresh();
      onOutsideChangeRef.current?.();
    },
    {
      enabled: !!householdId,
      channel: 'review-v0',
    },
  );

  useEffect(() => {
    if (!householdId) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void refresh();
      onOutsideChangeRef.current?.();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [householdId, refresh]);

  const items = useMemo(
    () =>
      data
        ? buildReviewList(data.items, data.targets, {
            timezone,
            children: data.children,
            targetsFailed: data.targetsFailed,
            openTasksByEvent: data.openTasks,
            hidden,
          })
        : [],
    [data, timezone, hidden],
  );
  const rows = useMemo(
    () => (data ? new Map(data.items.map((r) => [r.id, r])) : NO_ROWS),
    [data],
  );
  const children = data?.children ?? NO_CHILDREN;
  const count = useMemo(() => countItems(items), [items]);
  const gate = useMemo(() => confirmGateFor(items), [items]);

  const hide = useCallback((itemId: string) => {
    setHidden((prev) => new Set(prev).add(itemId));
  }, []);
  const unhide = useCallback((itemId: string) => {
    setHidden((prev) => {
      if (!prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  return {
    items,
    count,
    gate,
    loading,
    error,
    refresh,
    hide,
    unhide,
    rows,
    children,
  };
}
